/**
 * useFieldScoutDesktop — draft state, derivation, save/discard for desktop
 * paper-transcription recording mode.
 *
 * Non-sequential (differs from mobile useFieldScoutRecording):
 *   - enteredPoints is an explicit Set<number>, not a maxExamined watermark.
 *   - examined = enteredPoints.size (not maxExamined + 1).
 *   - No GPS / geolocation.
 *
 * Stale-closure safety: all mutable state lives in a single useReducer object.
 * Every action receives the LATEST state via the reducer, so no action ever
 * reads from a captured closure.
 *
 * Track: field_scout_recording_desktop / Slice 3 / Task 3.
 */
import { useReducer, useEffect, useCallback, useRef } from 'react';
import { db, type Pest } from '../../../../db';
import { saveMedia } from '../../../../services/mediaService';
import { reducePhoto } from '../../../../services/photoReduce';
import { syncPendingFieldScout } from '../../../../services/fieldScoutSyncService';
import { getSignedInTenantId } from '../../../../services/signedInTenant';
import { pct, tallyThresholdInForce } from '../../../../scouting/recording';
import { normalizeMeasurementType } from '../../../../scouting/pestConfig';
import { generateUUID } from '../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────
//  Public types
// ─────────────────────────────────────────────────────────────────────────────

export type CellState = 'empty' | 'clean' | 'infested';

// ─────────────────────────────────────────────────────────────────────────────
//  Pure helper — derive cell state from current draft data.
//  Used by the returned selector AND inside reducer transitions so there are
//  no stale-closure refs.
// ─────────────────────────────────────────────────────────────────────────────

const TREE_SIZE = 10;

// A point is either unexamined (every cell grey) or examined (every incidence
// pest has a verdict). Once a point is entered, an untouched pest reads 'clean'
// (green) — so selecting the first pest defaults the rest to clean. Multi-pest
// works because a clean cell cycles to 'infested' (see CYCLE_CELL), never back
// to 'empty'.
export function deriveCellState(
  entered: Set<number>,
  infested: Record<string, number[]>,
  point: number,
  pestId: string,
): CellState {
  if (!entered.has(point)) return 'empty';
  return (infested[pestId] ?? []).includes(point) ? 'infested' : 'clean';
}

// ─────────────────────────────────────────────────────────────────────────────
//  Reducer state
// ─────────────────────────────────────────────────────────────────────────────

interface DraftState {
  enteredPoints: Set<number>;
  infested: Record<string, number[]>;
  tallyInForce: Record<string, 'any' | '2+' | '3+'>;
  levels: Record<string, 0 | 1 | 2 | 3 | 4>;
  counts: Record<string, number>;
  photos: Record<number, number[]>;
  currentPoint: number;
  activePestId: string;
  mode: 'matrix' | 'point';
  gridMode: 'solo' | 'all';
  dateWalked: string;
}

type Action =
  | { type: 'HYDRATE'; state: DraftState }
  | { type: 'RESET'; activePestId: string }
  | { type: 'CYCLE_CELL'; point: number; pestId: string; tallyValue: 'any' | '2+' | '3+' }
  | { type: 'SET_VERDICT'; point: number; pestId: string; verdict: 'clean' | 'infested'; tallyValue: 'any' | '2+' | '3+' }
  | { type: 'MARK_POINT_CLEAN'; point: number }
  | { type: 'SET_CURRENT_POINT'; point: number }
  | { type: 'NEXT_EMPTY' }
  | { type: 'SET_DATE_WALKED'; date: string }
  | { type: 'RAISE_LEVEL'; pestId: string }
  | { type: 'LOWER_LEVEL'; pestId: string }
  | { type: 'INC_COUNT'; pestId: string }
  | { type: 'DEC_COUNT'; pestId: string }
  | { type: 'CAPTURE_PHOTO'; point: number; mediaId: number }
  | { type: 'SET_MODE'; mode: 'matrix' | 'point' }
  | { type: 'SET_GRID_MODE'; gridMode: 'solo' | 'all' }
  | { type: 'SET_ACTIVE_PEST'; pestId: string };

function reducer(state: DraftState, action: Action): DraftState {
  switch (action.type) {
    case 'HYDRATE':
      return action.state;

    case 'RESET': {
      const today = new Date().toISOString().slice(0, 10);
      return {
        enteredPoints: new Set<number>(),
        infested: {},
        tallyInForce: {},
        levels: {},
        counts: {},
        photos: {},
        currentPoint: 0,
        activePestId: action.activePestId,
        mode: 'matrix',
        gridMode: 'solo',
        dateWalked: today,
      };
    }

    case 'CYCLE_CELL': {
      const { point, pestId, tallyValue } = action;
      const current = deriveCellState(state.enteredPoints, state.infested, point, pestId);
      // Any interaction enters the point (so untouched pests default to clean).
      const nextEntered = new Set(state.enteredPoints);
      nextEntered.add(point);
      const nextTally = state.tallyInForce[pestId] !== undefined
        ? state.tallyInForce
        : { ...state.tallyInForce, [pestId]: tallyValue };

      if (current === 'infested') {
        // infested → clean (stays entered)
        const nextInfested = {
          ...state.infested,
          [pestId]: (state.infested[pestId] ?? []).filter((p) => p !== point),
        };
        return { ...state, infested: nextInfested, tallyInForce: nextTally, enteredPoints: nextEntered };
      }

      // empty OR clean → infested (a green cell goes to red; never back to grey)
      const nextInfested = {
        ...state.infested,
        [pestId]: [...(state.infested[pestId] ?? []), point],
      };
      return { ...state, infested: nextInfested, tallyInForce: nextTally, enteredPoints: nextEntered };
    }

    case 'SET_VERDICT': {
      const { point, pestId, verdict, tallyValue } = action;
      // Stamp tally only if not already recorded
      const nextTally = state.tallyInForce[pestId] !== undefined
        ? state.tallyInForce
        : { ...state.tallyInForce, [pestId]: tallyValue };
      const currentPestPoints = state.infested[pestId] ?? [];
      const nextPestPoints = verdict === 'infested'
        ? (currentPestPoints.includes(point) ? currentPestPoints : [...currentPestPoints, point])
        : currentPestPoints.filter((p) => p !== point);
      const nextInfested = { ...state.infested, [pestId]: nextPestPoints };
      // Always enter the point (an untouched pest then reads clean)
      const nextEntered = new Set(state.enteredPoints);
      nextEntered.add(point);
      return { ...state, infested: nextInfested, tallyInForce: nextTally, enteredPoints: nextEntered };
    }

    case 'MARK_POINT_CLEAN': {
      // Enter the point and clear every pest's infested verdict there → all clean.
      const { point } = action;
      const nextInfested: Record<string, number[]> = {};
      for (const [pid, pts] of Object.entries(state.infested)) {
        nextInfested[pid] = pts.filter((p) => p !== point);
      }
      const nextEntered = new Set(state.enteredPoints);
      nextEntered.add(point);
      return { ...state, infested: nextInfested, enteredPoints: nextEntered };
    }

    case 'SET_CURRENT_POINT':
      return { ...state, currentPoint: action.point };

    case 'NEXT_EMPTY': {
      // From currentPoint+1, find first point NOT in enteredPoints (0..99), else stay
      for (let i = state.currentPoint + 1; i <= 99; i++) {
        if (!state.enteredPoints.has(i)) {
          return { ...state, currentPoint: i };
        }
      }
      return state;
    }

    case 'SET_DATE_WALKED':
      return { ...state, dateWalked: action.date };

    case 'RAISE_LEVEL': {
      const current = (state.levels[action.pestId] ?? 0) as number;
      return {
        ...state,
        levels: { ...state.levels, [action.pestId]: Math.min(4, current + 1) as 0 | 1 | 2 | 3 | 4 },
      };
    }

    case 'LOWER_LEVEL': {
      const current = (state.levels[action.pestId] ?? 0) as number;
      return {
        ...state,
        levels: { ...state.levels, [action.pestId]: Math.max(0, current - 1) as 0 | 1 | 2 | 3 | 4 },
      };
    }

    case 'INC_COUNT':
      return {
        ...state,
        counts: { ...state.counts, [action.pestId]: (state.counts[action.pestId] ?? 0) + 1 },
      };

    case 'DEC_COUNT':
      return {
        ...state,
        counts: { ...state.counts, [action.pestId]: Math.max(0, (state.counts[action.pestId] ?? 0) - 1) },
      };

    case 'CAPTURE_PHOTO': {
      const { point, mediaId } = action;
      const nextPhotos = {
        ...state.photos,
        [point]: [...(state.photos[point] ?? []), mediaId],
      };
      const nextEntered = new Set(state.enteredPoints);
      nextEntered.add(point);
      return { ...state, photos: nextPhotos, enteredPoints: nextEntered };
    }

    case 'SET_MODE':
      return { ...state, mode: action.mode };

    case 'SET_GRID_MODE':
      return { ...state, gridMode: action.gridMode };

    case 'SET_ACTIVE_PEST':
      return { ...state, activePestId: action.pestId };

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Initial state (pre-hydration placeholder — never exposed to callers)
// ─────────────────────────────────────────────────────────────────────────────

function emptyDraftState(): DraftState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    enteredPoints: new Set<number>(),
    infested: {},
    tallyInForce: {},
    levels: {},
    counts: {},
    photos: {},
    currentPoint: 0,
    activePestId: '',
    mode: 'matrix',
    gridMode: 'solo',
    dateWalked: today,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useFieldScoutDesktop(
  orchardId: string,
  orchardName: string,
  scouterName: string,
  pests: Pest[],
) {
  const [loading, setLoadingRaw] = useReducer((_: boolean, v: boolean) => v, true);
  const [draftId, setDraftId] = useReducer((_: number | null, v: number | null) => v, null);
  const [state, dispatch] = useReducer(reducer, undefined, emptyDraftState);

  // Track hydration so autosave doesn't fire before the draft row exists.
  const hydratedRef = useRef(false);

  // ── Load / create on mount ─────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const loadDraft = async () => {
      if (!orchardId || !scouterName) {
        if (isMounted) setLoadingRaw(false);
        return;
      }

      try {
        setLoadingRaw(true);

        const existing = await db.fieldScoutDeskDrafts
          .where('[orchardId+scouterName+isComplete]')
          .equals([orchardId, scouterName, 0])
          .first();

        if (existing && isMounted) {
          const hydrated: DraftState = {
            enteredPoints: new Set(existing.enteredPoints),
            infested: existing.infested,
            tallyInForce: existing.tallyInForce as Record<string, 'any' | '2+' | '3+'>,
            levels: existing.levels as Record<string, 0 | 1 | 2 | 3 | 4>,
            counts: existing.counts,
            photos: existing.photos,
            currentPoint: existing.currentPoint,
            activePestId: existing.activePestId,
            mode: existing.mode,
            gridMode: existing.gridMode,
            dateWalked: existing.dateWalked,
          };
          dispatch({ type: 'HYDRATE', state: hydrated });
          setDraftId(existing.id!);
          hydratedRef.current = true;
        } else if (isMounted) {
          const firstIncidence =
            pests.find((p) => normalizeMeasurementType(p.measurementType) === 'incidence')?.id ?? '';
          const today = new Date().toISOString().slice(0, 10);
          const fresh: DraftState = {
            enteredPoints: new Set<number>(),
            infested: {},
            tallyInForce: {},
            levels: {},
            counts: {},
            photos: {},
            currentPoint: 0,
            activePestId: firstIncidence,
            mode: 'matrix',
            gridMode: 'solo',
            dateWalked: today,
          };
          dispatch({ type: 'HYDRATE', state: fresh });

          const id = await db.fieldScoutDeskDrafts.add({
            orchardId,
            orchardName,
            scouterName,
            dateWalked: today,
            mode: 'matrix',
            activePestId: firstIncidence,
            gridMode: 'solo',
            enteredPoints: [],
            infested: {},
            tallyInForce: {},
            levels: {},
            counts: {},
            photos: {},
            currentPoint: 0,
            startTime: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            isComplete: 0,
          });
          setDraftId(id as number);
          hydratedRef.current = true;
        }
      } catch (err) {
        console.error('[useFieldScoutDesktop] Failed to load draft:', err);
      } finally {
        if (isMounted) setLoadingRaw(false);
      }
    };

    loadDraft();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orchardId, scouterName, orchardName]);

  // ── Autosave on state change ───────────────────────────────────────────────
  useEffect(() => {
    if (!hydratedRef.current || draftId === null) return;

    db.fieldScoutDeskDrafts
      .update(draftId, {
        enteredPoints: Array.from(state.enteredPoints),
        infested: state.infested,
        tallyInForce: state.tallyInForce,
        levels: state.levels,
        counts: state.counts,
        photos: state.photos,
        currentPoint: state.currentPoint,
        activePestId: state.activePestId,
        mode: state.mode,
        gridMode: state.gridMode,
        dateWalked: state.dateWalked,
        lastUpdated: new Date().toISOString(),
      })
      .catch((err) => console.error('[useFieldScoutDesktop] Autosave failed:', err));
  }, [
    draftId,
    state.enteredPoints,
    state.infested,
    state.tallyInForce,
    state.levels,
    state.counts,
    state.photos,
    state.currentPoint,
    state.activePestId,
    state.mode,
    state.gridMode,
    state.dateWalked,
  ]);

  // ── Public actions ─────────────────────────────────────────────────────────

  const cycleCell = useCallback((point: number, pestId: string) => {
    const pest = pests.find((p) => p.id === pestId);
    const tallyValue = pest ? tallyThresholdInForce(pest) : 'any';
    dispatch({ type: 'CYCLE_CELL', point, pestId, tallyValue });
  }, [pests]);

  const setVerdict = useCallback((point: number, pestId: string, verdict: 'clean' | 'infested') => {
    const pest = pests.find((p) => p.id === pestId);
    const tallyValue = pest ? tallyThresholdInForce(pest) : 'any';
    dispatch({ type: 'SET_VERDICT', point, pestId, verdict, tallyValue });
  }, [pests]);

  const markPointClean = useCallback((point: number) => {
    dispatch({ type: 'MARK_POINT_CLEAN', point });
  }, []);

  const setCurrentPoint = useCallback((point: number) => {
    dispatch({ type: 'SET_CURRENT_POINT', point });
  }, []);

  const nextEmpty = useCallback(() => {
    dispatch({ type: 'NEXT_EMPTY' });
  }, []);

  const setDateWalked = useCallback((date: string) => {
    dispatch({ type: 'SET_DATE_WALKED', date });
  }, []);

  const raiseLevel = useCallback((pestId: string) => {
    dispatch({ type: 'RAISE_LEVEL', pestId });
  }, []);

  const lowerLevel = useCallback((pestId: string) => {
    dispatch({ type: 'LOWER_LEVEL', pestId });
  }, []);

  const incCount = useCallback((pestId: string) => {
    dispatch({ type: 'INC_COUNT', pestId });
  }, []);

  const decCount = useCallback((pestId: string) => {
    dispatch({ type: 'DEC_COUNT', pestId });
  }, []);

  const capturePhoto = useCallback(async (file: File) => {
    const mediaId = await saveMedia(0, await reducePhoto(file));
    dispatch({ type: 'CAPTURE_PHOTO', point: state.currentPoint, mediaId });
  }, [state.currentPoint]);

  const setMode = useCallback((mode: 'matrix' | 'point') => {
    dispatch({ type: 'SET_MODE', mode });
  }, []);

  const setGridMode = useCallback((gridMode: 'solo' | 'all') => {
    dispatch({ type: 'SET_GRID_MODE', gridMode });
  }, []);

  const setActivePest = useCallback((pestId: string) => {
    dispatch({ type: 'SET_ACTIVE_PEST', pestId });
  }, []);

  const saveDraft = useCallback(async () => {
    if (draftId === null) return;
    await db.fieldScoutDeskDrafts.update(draftId, {
      enteredPoints: Array.from(state.enteredPoints),
      infested: state.infested,
      tallyInForce: state.tallyInForce,
      levels: state.levels,
      counts: state.counts,
      photos: state.photos,
      currentPoint: state.currentPoint,
      activePestId: state.activePestId,
      mode: state.mode,
      gridMode: state.gridMode,
      dateWalked: state.dateWalked,
      lastUpdated: new Date().toISOString(),
    });
  }, [draftId, state]);

  const discard = useCallback(async () => {
    await db.fieldScoutDeskDrafts
      .where('[orchardId+scouterName+isComplete]')
      .equals([orchardId, scouterName, 0])
      .delete();
  }, [orchardId, scouterName]);

  const submit = useCallback(async (): Promise<string> => {
    const uuid = generateUUID();
    const timestamp = new Date().toISOString();

    // Desktop: date comes from operator-entered dateWalked (paper sheet date), not "now"
    const date = state.dateWalked || timestamp.slice(0, 10);

    // Build incidence rows: one per (pestId, infested point)
    const incidenceRows: Array<{
      pestId: string;
      inspectionPointId: number;
      infested: true;
      tallyThresholdInForce: string;
      timestamp: string;
    }> = [];

    for (const [pestId, points] of Object.entries(state.infested)) {
      for (const pointId of points) {
        incidenceRows.push({
          pestId,
          inspectionPointId: pointId,
          infested: true,
          tallyThresholdInForce: state.tallyInForce[pestId] ?? 'any',
          timestamp,
        });
      }
    }

    // Build level rows (severity > 0 only)
    const levelRows = Object.entries(state.levels)
      .filter(([, v]) => v > 0)
      .map(([pestId, v]) => ({ pestId, valueSeverity: v, timestamp }));

    // Build count rows (count > 0 only)
    const countRows = Object.entries(state.counts)
      .filter(([, v]) => v > 0)
      .map(([pestId, v]) => ({ pestId, valueCount: v, timestamp }));

    // Photo manifest grouped by inspection point
    const photoManifest = Object.entries(state.photos)
      .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
      .map(([pointId, ids]) => ({ inspectionPointId: Number(pointId), mediaIds: ids }));

    // Desktop: examinedCount = enteredPoints.size (non-sequential)
    const payload = {
      blockId: orchardName,
      orchardName,
      uuid,
      date,
      scouterName,
      examinedCount: state.enteredPoints.size,
      totalInspectionPoints: 100,
      incidence: incidenceRows,
      level: levelRows,
      count: countRows,
      photos: photoManifest,
    };

    // Collect all photo mediaIds for re-linking
    const allMediaIds = Object.values(state.photos).flat();

    // Enqueue for sync
    await db.fieldScoutQueue.add({
      uuid,
      payload,
      syncStatus: 'pending',
      createdAt: timestamp,
      tenantId: getSignedInTenantId() ?? undefined,
    });

    // Re-link media rows to the queue row so they are not orphaned
    if (allMediaIds.length > 0) {
      const queueItem = await db.fieldScoutQueue
        .where('uuid')
        .equals(uuid)
        .first();
      if (queueItem?.id !== undefined) {
        await db.media
          .where('id')
          .anyOf(allMediaIds)
          .modify({ sessionId: queueItem.id });
      }
    }

    // Mark the current desk draft complete
    if (draftId !== null) {
      await db.fieldScoutDeskDrafts.update(draftId, {
        isComplete: 1,
        lastUpdated: timestamp,
        queueUuid: uuid,
      });
    }

    // Create a fresh desk draft and reset reducer state
    const firstIncidence =
      pests.find((p) => normalizeMeasurementType(p.measurementType) === 'incidence')?.id ?? '';

    const today = new Date().toISOString().slice(0, 10);
    const newDraftId = await db.fieldScoutDeskDrafts.add({
      orchardId,
      orchardName,
      scouterName,
      dateWalked: today,
      mode: 'matrix',
      activePestId: firstIncidence,
      gridMode: 'solo',
      enteredPoints: [],
      infested: {},
      tallyInForce: {},
      levels: {},
      counts: {},
      photos: {},
      currentPoint: 0,
      startTime: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      isComplete: 0,
    });

    setDraftId(newDraftId as number);
    dispatch({ type: 'RESET', activePestId: firstIncidence });

    // Trigger sync — fire and forget (offline-safe)
    syncPendingFieldScout().catch((err) =>
      console.error('[useFieldScoutDesktop] sync trigger failed:', err),
    );

    return uuid;
  }, [
    orchardId,
    orchardName,
    scouterName,
    draftId,
    state,
    pests,
  ]);

  // ── Derived selectors ──────────────────────────────────────────────────────

  const cellState = useCallback((point: number, pestId: string): CellState => {
    return deriveCellState(state.enteredPoints, state.infested, point, pestId);
  }, [state.enteredPoints, state.infested]);

  const examined = state.enteredPoints.size;

  const pctFor = useCallback((pestId: string): number => {
    return pct((state.infested[pestId] ?? []).length, examined);
  }, [state.infested, examined]);

  const treeInfestedCount = useCallback((tree: number, pestId: string): number => {
    const lo = tree * TREE_SIZE;
    const hi = lo + TREE_SIZE;
    return (state.infested[pestId] ?? []).filter((p) => p >= lo && p < hi).length;
  }, [state.infested]);

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // Meta
    loading,
    // State slices
    enteredPoints: state.enteredPoints,
    infested: state.infested,
    tallyInForce: state.tallyInForce,
    levels: state.levels,
    counts: state.counts,
    photos: state.photos,
    currentPoint: state.currentPoint,
    activePestId: state.activePestId,
    mode: state.mode,
    gridMode: state.gridMode,
    dateWalked: state.dateWalked,
    // Derived
    examined,
    cellState,
    pctFor,
    treeInfestedCount,
    // Actions
    cycleCell,
    setVerdict,
    markPointClean,
    setCurrentPoint,
    nextEmpty,
    setDateWalked,
    raiseLevel,
    lowerLevel,
    incCount,
    decCount,
    capturePhoto,
    setMode,
    setGridMode,
    setActivePest,
    saveDraft,
    discard,
    submit,
  };
}

export default useFieldScoutDesktop;
